/**
 * The secret room behind the red doors (spec 02 §9.3, research R§7 "Secret doors and rooms"): one
 * design, drawn in blue, no monsters, no time limit, 36 diamonds. The C64's own character art for it
 * is not documented [U], so this is a plain room in the game's own terms: walls, three shelves the
 * players jump up through, 36 diamonds along the floor and the shelves, and a two-tile exit in the
 * middle of the floor that opens once 24 are taken. It is level 0: no level number the game uses.
 */

import type { LevelDef, TilePos } from './types';

const COLS = 32;
const ROWS = 25;
/** The shelves' rows (their top tile row); the floor is row 24. */
export const SECRET_SHELVES: readonly number[] = [6, 12, 18];
/** The floor's exit: these columns of the bottom row open when enough diamonds are taken. */
export const SECRET_EXIT_COLS: readonly number[] = [15, 16];

function tiles(): string[] {
  const rows: string[] = [];
  for (let r = 0; r < ROWS; r++) {
    let row = '';
    for (let c = 0; c < COLS; c++) {
      const wall = c < 2 || c >= COLS - 2 || r === 0 || r === ROWS - 1;
      // Each shelf leaves a gap by each wall to drop through.
      const shelf = SECRET_SHELVES.includes(r) && c >= 5 && c < COLS - 5;
      row += wall || shelf ? '#' : '.';
    }
    rows.push(row);
  }
  return rows;
}

/** Where the 36 diamonds stand: 2x2-tile items (top-left tile), on the shelves and the floor. */
export function secretDiamonds(): TilePos[] {
  const out: TilePos[] = [];
  SECRET_SHELVES.forEach((top, i) => {
    for (const x of i === SECRET_SHELVES.length - 1 ? LOW_SHELF_COLS : SHELF_COLS) out.push({ x, y: top - 2 });
  });
  for (const x of FLOOR_COLS) out.push({ x, y: ROWS - 3 });
  return out;
}

/**
 * Diamond columns: nine along the upper shelves (columns 5-26), ten along the lowest, and eight on the
 * floor either side of the exit, clear of where the players start (columns 3-4 and 27-28).
 */
const SHELF_COLS: readonly number[] = [5, 8, 10, 13, 15, 18, 20, 23, 25];
const LOW_SHELF_COLS: readonly number[] = [6, 8, 10, 12, 14, 16, 18, 20, 22, 24];
const FLOOR_COLS: readonly number[] = [6, 8, 10, 12, 18, 20, 22, 24];

export const SECRET_ROOM: LevelDef = {
  n: 0,
  tiles: tiles(),
  wind: Array.from({ length: ROWS }, () => '^'.repeat(COLS)),
  monsters: [],
  items: { points: { x: 15, y: 22 }, powerup: { x: 15, y: 22 } },
  specials: [],
  // C64 light blue and blue.
  colours: [14, 6],
};
